import { useSeo } from "@/lib/seo";

import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, Repeat, Eye, Calendar, BadgeCheck, ThumbsUp, MessageCircle, ChevronLeft, ChevronRight, Trash2, Flag, Truck, Hand, WalletCards, Award } from "lucide-react";
import { CARD_TYPE_LABELS, CARD_CONDITION_LABELS } from "@/lib/cards";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import TradeProposalModal from "@/components/TradeProposalModal";
import MutualConnections from "@/components/MutualConnections";
import VerificationBadges, { ReputationLine } from "@/components/VerificationBadges";

const CONDITION_LABELS = { new: "New", like_new: "Like new", good: "Good", fair: "Fair" };

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchWith = searchParams.get("match_with");
  const [listing, setListing] = useState(null);
  const [imageIdx, setImageIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const seoImg = listing?.images?.[0]
    ? (listing.images[0].startsWith("http") ? listing.images[0] : `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`)
    : undefined;
  useSeo(listing ? {
    title: `${listing.title} — For Trade in ${listing.location} | Vyrti`,
    description: `${listing.title} available for trade on Vyrti. ${(listing.description || "").slice(0, 140)}${listing.wants?.length ? ` Looking for: ${listing.wants.slice(0, 3).join(", ")}.` : ""}`,
    path: `/listing/${listing.id}`,
    image: seoImg,
    type: "product",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: listing.title,
      description: listing.description,
      category: listing.category,
      image: seoImg,
      itemCondition: listing.condition === "new" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
      brand: { "@type": "Organization", name: "Vyrti" },
      offers: {
        "@type": "Offer",
        url: `https://vyrti.com/listing/${listing.id}`,
        availability: "https://schema.org/InStock",
        price: "0",
        priceCurrency: "USD",
        description: "Trade only — no cash. Propose an item swap on Vyrti.",
      },
    },
  } : {});

  useEffect(() => {
    setLoading(true);
    api.get(`/listings/${id}`).then(({ data }) => setListing(data))
      .catch(() => toast.error("Listing not found"))
      .finally(() => setLoading(false));
  }, [id]);

  // Deep-link from weekly digest email: auto-open the propose modal with recipient's
  // best-matching own listing pre-selected. Fires once per mount when the listing + user
  // are loaded, and only if the target isn't the viewer's own listing.
  useEffect(() => {
    if (autoOpened) return;
    if (!matchWith || !listing || !user) return;
    if (user.user_id === listing.user_id) return;
    setProposalOpen(true);
    setAutoOpened(true);
  }, [matchWith, listing, user, autoOpened]);

  useEffect(() => {
    if (!listing?.owner?.user_id) return;
    api.get(`/users/${listing.owner.user_id}/feedback-summary`)
      .then(({ data }) => setFeedback(data))
      .catch(() => setFeedback(null));
  }, [listing?.owner?.user_id]);

  const isOwner = user && listing && user.user_id === listing.user_id;

  const handleDelete = async () => {
    if (!window.confirm("Remove this listing?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing removed");
      navigate("/me");
    } catch (e) { toast.error("Could not remove"); }
  };

  const handleProposeClick = () => {
    if (!user) { navigate("/login"); return; }
    setProposalOpen(true);
  };

  const handleSetCover = async () => {
    try {
      const { data } = await api.post(`/listings/${id}/cover`, { index: imageIdx });
      setListing(data);
      setImageIdx(0);
      toast.success("Cover photo updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update cover");
    }
  };

  const handleReport = async () => {
    if (!user) { navigate("/login"); return; }
    const reason = window.prompt("Why are you reporting this listing? (e.g. scam, inappropriate, prohibited item)");
    if (!reason) return;
    const details = window.prompt("Add any details (optional):") || "";
    try {
      await api.post("/reports", { target_type: "listing", target_id: id, reason, details });
      toast.success("Report submitted. Thanks — our team will review.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report");
    }
  };

  if (loading) {
    return (
      <div className="section-pad py-12">
        <div className="grid md:grid-cols-12 gap-4 mb-8">
          <div className="md:col-span-8 aspect-[4/3] skeleton rounded-sm" />
          <div className="hidden md:flex md:col-span-4 flex-col gap-4">
            <div className="flex-1 skeleton rounded-sm" />
            <div className="flex-1 skeleton rounded-sm" />
          </div>
        </div>
        <div className="skeleton h-12 w-2/3 rounded-sm mb-3" />
        <div className="skeleton h-4 w-1/3 rounded-sm" />
      </div>
    );
  }
  if (!listing) return <div className="section-pad py-12 font-display text-2xl text-stone-700">Not found.</div>;

  const images = listing.images || [];
  const wants = listing.wants || [];
  const underReview = listing.status === "under_review";
  const cd = listing.card_details;

  return (
    <div className="section-pad py-6 md:py-10" data-testid="listing-detail">
      <button onClick={() => navigate(-1)} className="text-xs font-semibold text-slate-500 hover:text-[#0654BA] mb-5 inline-flex items-center gap-1 transition-colors" data-testid="back-button">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to results
      </button>

      {underReview && isOwner && (
        <div className="mb-6 bg-amber-50 border border-amber-300 rounded-md p-4" data-testid="under-review-banner">
          <p className="text-sm font-semibold text-amber-900">Under review</p>
          <p className="text-xs text-amber-800 mt-1">Our team is verifying this listing follows Vyrti&apos;s content rules. You&apos;ll be notified once it&apos;s published.</p>
        </div>
      )}

      {/* ====== Bento gallery + side thumbs ====== */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 mb-10 md:mb-14">
        <div
          className={`relative col-span-1 ${images.length > 1 ? "md:col-span-8" : "md:col-span-12"} aspect-[4/3] md:aspect-auto md:h-[480px] lg:h-[560px] rounded-sm overflow-hidden bg-[#F1F5F9]`}
          data-testid="listing-gallery"
        >
          {images[imageIdx] && (
            <img src={imgSrc(images[imageIdx])} alt={listing.title} className="w-full h-full object-cover" />
          )}
          {images.length > 1 && (
            <>
              <button onClick={() => setImageIdx((imageIdx - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white border border-[#E2E2E2]" data-testid="gallery-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setImageIdx((imageIdx + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white border border-[#E2E2E2]" data-testid="gallery-next">
                <ChevronRight className="w-4 h-4" />
              </button>
              <span className="absolute bottom-4 left-4 vyrti-pill !bg-white/95">{imageIdx + 1} / {images.length}</span>
            </>
          )}
          {listing.open_to_offers && (
            <span className="absolute top-4 left-4 vyrti-pill !bg-white/95">
              <Repeat className="w-3 h-3" /> Open to offers
            </span>
          )}
          {isOwner && images.length > 1 && imageIdx !== 0 && (
            <button onClick={handleSetCover} className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-xs font-semibold border border-[#E2E2E2] hover:bg-white transition-colors" data-testid="set-cover-button">
              Set as cover
            </button>
          )}
        </div>

        {/* Side thumbnails — desktop only, max 2 others */}
        {images.length > 1 && (
          <div className="hidden md:flex col-span-4 flex-col gap-4">
            {images
              .map((src, i) => ({ src, i }))
              .filter(o => o.i !== imageIdx)
              .slice(0, 2)
              .map((o, idx) => (
                <button
                  key={o.i}
                  onClick={() => setImageIdx(o.i)}
                  className="aspect-square rounded-sm overflow-hidden bg-[#F1F5F9] group/thumb"
                  data-testid={`thumb-${idx}`}
                >
                  <img src={imgSrc(o.src)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover/thumb:scale-105" />
                </button>
              ))}
          </div>
        )}

        {/* Mobile thumb strip */}
        {images.length > 1 && (
          <div className="md:hidden flex gap-2 overflow-x-auto no-scrollbar col-span-1">
            {images.map((src, i) => (
              <button key={`mob-thumb-${src}-${i}`} onClick={() => setImageIdx(i)} className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border ${i === imageIdx ? "border-[#0654BA]" : "border-transparent opacity-70"}`} data-testid={`mobile-thumb-${i}`}>
                <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ====== Title block — marketplace style ====== */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-8">
        <div className="md:col-span-8">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="vyrti-pill">{listing.category}</span>
            {cd?.game && (
              <span className="vyrti-pill !bg-[#FFF6DE] !text-[#92400E]" data-testid="detail-card-game">
                <WalletCards className="w-3 h-3" /> {cd.game}
              </span>
            )}
            {cd?.graded && cd?.grade && (
              <span className="vyrti-pill !bg-slate-900 !text-white" data-testid="detail-card-grade">
                <Award className="w-3 h-3 text-[#F5AF02]" /> {`${cd.grading_company || ""} ${cd.grade}`.trim()}
              </span>
            )}
            {cd && !cd.graded && cd.card_condition && (
              <span className="vyrti-pill" data-testid="detail-card-condition">Raw · {CARD_CONDITION_LABELS[cd.card_condition] || cd.card_condition}</span>
            )}
            {cd?.card_type && <span className="vyrti-pill">{CARD_TYPE_LABELS[cd.card_type] || cd.card_type}</span>}
            <span className="vyrti-pill">{CONDITION_LABELS[listing.condition] || listing.condition}</span>
            {listing.delivery && (
              <span className="vyrti-pill !bg-[#E0EDFA] !text-[#0654BA]">
                {listing.delivery === "local" ? <><Hand className="w-3 h-3" /> Local pickup</> :
                 listing.delivery === "shipping" ? <><Truck className="w-3 h-3" /> Ships</> :
                 <><Repeat className="w-3 h-3" /> Local · Ships</>}
              </span>
            )}
          </div>
          <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight mb-3">{listing.title}</h1>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4"/> {listing.location}</span>
            <span className="flex items-center gap-1.5"><Eye className="w-4 h-4"/> {listing.views || 0} views</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4"/> Listed {new Date(listing.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Owner card — quiet right column */}
        <div className="md:col-span-4 space-y-3">
          {listing.owner && (
            <Link to={`/profile/${listing.owner.user_id}`} className="vyrti-card p-4 block" data-testid="listing-owner">
              <div className="flex items-center gap-3">
                {listing.owner.picture ? (
                  <img src={imgSrc(listing.owner.picture)} alt={listing.owner.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-700">{listing.owner.name?.[0]?.toUpperCase()}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-wider uppercase text-slate-500 font-semibold mb-0.5">Offered by</p>
                  <p className="font-semibold flex items-center gap-1.5 text-slate-900">
                    {listing.owner.name}
                    {listing.owner.verified && <BadgeCheck className="w-4 h-4 text-[#0654BA]" />}
                  </p>
                  <div className="mt-1.5">
                    <ReputationLine summary={feedback} />
                  </div>
                </div>
              </div>
              {feedback?.badges && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <VerificationBadges badges={feedback.badges} />
                </div>
              )}
            </Link>
          )}

          {/* Mutual connections — LinkedIn-style trust signal */}
          {listing.owner && !isOwner && (
            <MutualConnections userId={listing.owner.user_id} ownerName={listing.owner.name} />
          )}
        </div>
      </div>

      {/* ====== Body — wants + description + CTA ====== */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-12">
        <div className="md:col-span-8 space-y-8">
          {wants.length > 0 && (
            <div className="vyrti-card p-5">
              <p className="eyebrow text-slate-500 mb-3">Looking to trade for</p>
              <div className="flex flex-wrap gap-2">
                {wants.map((w, i) => <span key={`want-${w}-${i}`} className="bg-[#E0EDFA] text-[#0654BA] px-3 py-1.5 rounded-md text-sm font-semibold" data-testid={`want-${i}`}>{w}</span>)}
                {listing.open_to_offers && <span className="bg-[#FFF6DE] text-[#92400E] px-3 py-1.5 rounded-md text-sm font-semibold">+ Open to other offers</span>}
              </div>
            </div>
          )}

          {cd && (
            <div className="vyrti-card p-5" data-testid="card-details-block">
              <p className="eyebrow text-slate-500 mb-3 flex items-center gap-1.5"><WalletCards className="w-3.5 h-3.5" /> Card details</p>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                {cd.game && <div><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Game / Sport</dt><dd className="font-semibold text-slate-900 mt-0.5">{cd.game}</dd></div>}
                {cd.card_type && <div><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Type</dt><dd className="font-semibold text-slate-900 mt-0.5">{CARD_TYPE_LABELS[cd.card_type] || cd.card_type}</dd></div>}
                <div><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Graded</dt><dd className="font-semibold text-slate-900 mt-0.5">{cd.graded ? `Yes — ${cd.grading_company || ""} ${cd.grade || ""}`.trim() : "No (raw)"}</dd></div>
                {!cd.graded && cd.card_condition && <div><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Condition</dt><dd className="font-semibold text-slate-900 mt-0.5">{CARD_CONDITION_LABELS[cd.card_condition] || cd.card_condition}</dd></div>}
                {cd.set_name && <div><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Set</dt><dd className="font-semibold text-slate-900 mt-0.5">{cd.set_name}</dd></div>}
                {cd.year && <div><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Year</dt><dd className="font-semibold text-slate-900 mt-0.5">{cd.year}</dd></div>}
              </dl>
            </div>
          )}

          <div>
            <p className="eyebrow text-slate-500 mb-3">About this item</p>
            <p className="text-slate-700 whitespace-pre-line leading-relaxed text-[15px] md:text-base max-w-3xl">{listing.description}</p>
          </div>
        </div>

        {/* CTA column — sticky on desktop */}
        <div className="md:col-span-4">
          <div className="md:sticky md:top-24">
            {isOwner ? (
              <div className="vyrti-card p-5 space-y-3">
                <p className="text-sm text-slate-600 font-medium">This is your listing.</p>
                <Link to={`/what-can-i-get?item_id=${listing.id}`} className="btn-block btn-accent w-full" data-testid="listing-detail-match-cta">
                  <Repeat className="w-4 h-4" /> See what you can get
                </Link>
                <button onClick={handleDelete} className="btn-block btn-ghost w-full !text-rose-700 !border-rose-300 hover:!bg-rose-50" data-testid="listing-remove">
                  <Trash2 className="w-4 h-4" /> Remove listing
                </button>
              </div>
            ) : (
              <div className="vyrti-card p-5 flex flex-col gap-3">
                <button onClick={handleProposeClick} className="btn-block btn-accent !text-base !py-3.5 w-full" data-testid="propose-trade-button">
                  <Repeat className="w-4 h-4" />
                  Propose a trade
                </button>
                <p className="text-[11px] text-slate-500 text-center">No cash · Just trade.</p>
                <button onClick={handleReport} className="text-xs text-slate-500 hover:text-rose-700 inline-flex items-center justify-center gap-1 mt-1" data-testid="report-listing-button">
                  <Flag className="w-3 h-3" /> Report this listing
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <TradeProposalModal
        open={proposalOpen}
        onClose={() => setProposalOpen(false)}
        targetListing={listing}
        preselectedIds={matchWith ? [matchWith] : []}
      />
    </div>
  );
}
